import { Component, Input, Output, OnInit, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { LoggerService } from "@teammood/shared/util/logger";
import { MoodsService } from "../../../timeline/timeline-item/moods/moods.service";
import { DaysService } from "../../../timeline/daily-overview/days.service";
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged, tap } from "rxjs";
import { GifPreview, LinkPreview, Mood, MoodValue } from "@teammood/timeline";


@Component({
    selector: 'add-mood',
    templateUrl: './add-mood.component.html',
    providers: [MoodsService, DaysService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddMoodComponent implements OnInit {
    
    moodSaved: boolean;
    placeholder: string;
    codeOfConductLabel: string;
    linkPreviews: LinkPreview[] = [];
    gifPreviews: GifPreview[] = [];
    moodCommentCtrl = new FormControl<string>('');

    @Input() mood: Mood;
    @Input() previousComment: string;

    @Output()
    onMoodUpdated: EventEmitter<Mood> = new EventEmitter<Mood>();

    constructor(
        private log: LoggerService,
        private translate: TranslateService, 
        private moodsService: MoodsService, 
        private daysService: DaysService, 
        private snackBarService: SnackBarService,
    ) {}

    ngOnInit() {
        this.moodCommentCtrl.setValue(this.mood.comment?.body);
        this.moodSaved = true;
        this.mood.commentSaved = true; // boolean to enable save btn and for the parent to display confirm modal
        this.placeholder = this.getCommentPlaceholder(this.mood.mood);
        this.codeOfConductLabel = this.translate.instant('add_mood_box.code_of_conduct');

        // restore previous comment (for multiteam) if no comment written yet
        if(this.mood.comment == undefined && this.previousComment){
            this.mood.comment = {
                body: this.previousComment,
                linkPreviews: []
            };
            this.moodCommentCtrl.setValue(this.previousComment);
        }

        this.moodCommentCtrl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(async () => {
                this.mood.comment = {
                    body: this.moodCommentCtrl.getRawValue(),
                    linkPreviews: []
                };

                this.mood.commentSaved = false;
                if(this.mood.comment.body.includes('http')){
                    await this.get_link_previews();
                }
            }),
        ).subscribe();
    }

    private getCommentPlaceholder (moodValue: string): string {
        switch (moodValue){
            case 'excellent':
                return 'add_comment_placeholder_excellent';
            case 'good':
                return 'add_comment_placeholder_good';
            case 'average':
                return 'add_comment_placeholder_average';
            case 'hard':
                return 'add_comment_placeholder_hard';
            case 'bad':
                return 'add_comment_placeholder_bad';
            default:
                return 'add_comment_placeholder';
        }
    }

    updateMood = async (mood: MoodValue) => {
        this.moodSaved = false;
        this.mood.mood = mood;
        await this.save_mood();
        this.moodSaved = true;
        this.placeholder = this.getCommentPlaceholder(mood);
    };

    async updateComment() {
        this.mood.commentSaved = false;
        await this.save_mood();
        this.snackBarService.success(this.translate.instant('add_comment_comment_published'));
        this.mood.commentSaved = true;

    };

    deleteComment (){
        this.mood.comment.body = '';
        this.moodCommentCtrl.setValue('');
        this.linkPreviews = [];
        this.gifPreviews = [];
    };

    async save_mood () {
        if (this.mood && this.mood.id) {
            try{
                return await this.daysService.updateMood(this.mood);
            } catch(error){
                this.log.error('Error updating mood:', error);
            }
        } else {
            return await this.daysService.createMood(this.mood.mood, this.mood.teamId)
                .then((result: any) => {
                    this.mood = result.event.item;
                    this.onMoodUpdated.next(this.mood);
                });
        }
    }

    async get_link_previews () {
        if (this.mood &&  this.mood.comment) {
            const previews : LinkPreview[] = (await this.moodsService.getCommentLinkPreviews(this.mood.comment.body));
            this.linkPreviews = previews.filter(preview => preview.title && preview.description);
            this.gifPreviews = previews.filter(preview => preview.url.slice(-4) === '.gif').map(preview => new GifPreview(preview));
        } else {
             this.linkPreviews = [];
             this.gifPreviews = [];
        }
    };

    fromMoodPicker (element: string) {
        if (this.mood.comment && this.mood.comment.body) {
            this.moodCommentCtrl.setValue(this.mood.comment.body + ' ' + element);
        } else {
            this.moodCommentCtrl.setValue(element);
        }
    }    
}
